/**
 * La position d'un accord : quelle note il a sous la main gauche.
 *
 * ## Ce que le niveau 2 enseigne, et que l'écran de jeu ne disait pas
 *
 * Le sas du niveau 2 pose le mot : « Selon la note du bas, on dit que l'accord
 * est en état fondamental, en premier renversement ou en deuxième
 * renversement. C'est sa position. » Puis l'écran de jeu montrait la chose sans
 * jamais la nommer : les trois lettres changent d'ordre, et le joueur voyait
 * bien que quelque chose bougeait sans avoir le nom sous les yeux au moment où
 * il le vivait.
 *
 * ## Rien pour l'état fondamental
 *
 * C'est la position par défaut, celle de tout le niveau 1, et la nommer sur
 * chaque accord ferait du mot un décor : il n'apparaîtrait jamais avec un sens,
 * parce qu'il serait toujours là. Le mot ne sort que quand la position change,
 * donc quand il apprend quelque chose. Le niveau 1 ne l'affiche donc jamais,
 * sans que rien n'ait à savoir de quel niveau il s'agit.
 *
 * ## Elle se lit sur la main droite, et sur elle seule
 *
 * Le sas le dit : « Ici, le renversement est dans la main droite : la gauche
 * tient toujours la fondamentale, et c'est pour ça que le nom de l'accord ne
 * change pas. » L'inversion que `PhraseChord` dérive porte, elle, sur la note
 * la plus grave des **deux** mains, ce qui est la bonne réponse à une autre
 * question, celle du rail et de sa basse écrite. Aux niveaux fabriqués
 * la gauche joue la fondamentale, donc cette inversion-là vaut zéro sur tous
 * les accords et ne dirait jamais rien.
 *
 * ## Le rang se lit sur la pile, sans table d'intervalles
 *
 * Une pile de trois notes est une rotation : fondamentale-tierce-quinte,
 * tierce-quinte-fondamentale, quinte-fondamentale-tierce. Le rang de la
 * fondamentale dans la pile montante suffit donc à nommer la position, et la
 * règle vaut pour un empilement de quatre notes sans qu'on ait à connaître la
 * qualité de l'accord.
 *
 * ## Les mots du sas, pas une abréviation
 *
 * « Premier renversement » en toutes lettres et non « 1er renv. » : le sas
 * enseigne ces mots-là quelques secondes plus tôt, et une abréviation obligerait
 * le joueur à faire le lien entre deux formes de la même chose au moment où il
 * découvre la chose elle-même.
 */

/**
 * Le rang de la position d'un empilement, la fondamentale valant zéro.
 *
 * `0` aussi quand la fondamentale n'est pas dans la pile : un accord sans sa
 * fondamentale n'a pas de renversement à nommer, et deviner en nommerait un
 * faux. Les classes de hauteur sont dédoublonnées avant de compter, sans quoi
 * une octave doublée décalerait le rang.
 */
export function chordPosition(
  rootPitchClass: number,
  midis: readonly number[],
): number {
  const classes: number[] = [];

  for (const midi of [...midis].sort((a, b) => a - b)) {
    const pitchClass = ((midi % 12) + 12) % 12;

    if (!classes.includes(pitchClass)) {
      classes.push(pitchClass);
    }
  }

  const at = classes.indexOf(((rootPitchClass % 12) + 12) % 12);

  return at <= 0 ? 0 : classes.length - at;
}

/**
 * Le nom de la position, ou `null` quand il n'y a rien à dire.
 *
 * `null` sur l'état fondamental, et sur tout ce que la dérivation ne sait pas
 * nommer : elle rend `0` quand la qualité est inconnue ou la fenêtre muette,
 * donc le silence couvre déjà ces cas, et un rang au-delà du troisième
 * renversement n'existe sur aucun accord que ce parcours fait jouer.
 */
export function positionLabel(inversion: number): string | null {
  switch (inversion) {
    case 1:
      return "Premier renversement";
    case 2:
      return "Deuxième renversement";
    case 3:
      return "Troisième renversement";
    default:
      return null;
  }
}

/**
 * Le nom de la position, état fondamental compris.
 *
 * `positionLabel` se tait sur l'état fondamental, et c'est juste là où il sert :
 * une pastille posée sur chaque accord ferait du mot un décor. Le sas, lui,
 * présente les trois positions côte à côte avant qu'on en joue aucune, et une
 * ligne sans nom n'y voudrait rien dire. Dérivé et non recopié : deux tables
 * finiraient par ne plus nommer la même chose.
 *
 * « État fondamental » et non « Fondamentale » : la fondamentale est une
 * **note**, celle qui donne son nom à l'accord, et la fiche du sas écrit les
 * deux à trois centimètres l'une de l'autre.
 */
export function positionName(inversion: number): string {
  return positionLabel(inversion) ?? "État fondamental";
}

/**
 * La position, en trois signes au lieu de trois mots.
 *
 * « Deuxième renversement » se lit en une seconde et demie, écrit sous le nom
 * d'un accord dans une petite capitale grise. Ce n'est pas assez : à ce
 * moment-là le joueur regarde le rail de rythme, et il n'a qu'un coup d'œil à
 * donner au reste. La forme brève tient dans une pastille assez grande pour se
 * lire depuis le pupitre.
 *
 * La position fondamentale se nomme, elle aussi. Elle se taisait quand
 * l'information était une ligne de texte, où le silence voulait dire « rien à
 * signaler » ; sur une pastille de couleur, se taire voudrait dire « on ne sait
 * pas », et laisserait un trou là où la géométrie de l'écran demande un objet.
 */
export function positionShort(inversion: number): string {
  switch (inversion) {
    case 1:
      return "1er renv.";
    case 2:
      return "2e renv.";
    case 3:
      return "3e renv.";
    default:
      return "Fond.";
  }
}
